// Color difference metrics in Lab space.
// CIE76 (1976) and CIEDE2000 (Sharma et al. 2005).
// All angles in radians internally; degrees converted where the standard requires.

import type { LabColor } from '../types';

export function deltaE76(lab1: LabColor, lab2: LabColor): number {
  const dL = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

/**
 * CIEDE2000 color difference (dE00).
 * Implemented per Sharma, Wu, Dalal (2005), "The CIEDE2000 color-difference
 * formula: Implementation notes, supplementary test data, and mathematical observations".
 * 
 * Fixes vs sketch (blocker #3):
 *  - T term uses cos(h - pi/6) and cos(3h + pi/30) (not the incorrect sketch version)
 *  - Mean-hue wrap-around: if |hp1 - hp2| > pi, add 2pi to sum before averaging
 *  - No unused variables
 */
export function deltaE2000(lab1: LabColor, lab2: LabColor): number {
  const L1 = lab1.L;
  const a1 = lab1.a;
  const b1 = lab1.b;
  const L2 = lab2.L;
  const a2 = lab2.a;
  const b2 = lab2.b;

  const Lbar = (L1 + L2) / 2;
  const C1 = Math.sqrt(a1 * a1 + b1 * b1);
  const C2 = Math.sqrt(a2 * a2 + b2 * b2);
  const Cabbar = (C1 + C2) / 2;

  const G =
    0.5 * (1 - Math.sqrt(Math.pow(Cabbar, 7) / (Math.pow(Cabbar, 7) + Math.pow(25, 7))));

  const ap1 = (1 + G) * a1;
  const ap2 = (1 + G) * a2;
  const Cp1 = Math.sqrt(ap1 * ap1 + b1 * b1);
  const Cp2 = Math.sqrt(ap2 * ap2 + b2 * b2);
  const Cabpbar = (Cp1 + Cp2) / 2;

  // Hue angles in [0, 2pi)
  let hp1 = Math.atan2(b1, ap1);
  if (hp1 < 0) hp1 += 2 * Math.PI;
  let hp2 = Math.atan2(b2, ap2);
  if (hp2 < 0) hp2 += 2 * Math.PI;

  const dLp = L2 - L1;
  const dCp = Cp2 - Cp1;

  let dhp = hp2 - hp1;
  if (Math.abs(dhp) > Math.PI) {
    dhp = dhp > 0 ? dhp - 2 * Math.PI : dhp + 2 * Math.PI;
  }
  const dHp = 2 * Math.sqrt(Cp1 * Cp2) * Math.sin(dhp / 2);

  const SL = 1 + (0.015 * Math.pow(Lbar - 50, 2)) / Math.sqrt(20 + Math.pow(Lbar - 50, 2));
  const SC = 1 + 0.045 * Cabpbar;

  // Mean hue with wrap-around handling (blocker #3)
  let hbar: number;
  if (Math.abs(hp1 - hp2) > Math.PI) {
    hbar = (hp1 + hp2 + 2 * Math.PI) / 2;
  } else {
    hbar = (hp1 + hp2) / 2;
  }
  hbar = hbar % (2 * Math.PI);
  const hbarDeg = (hbar * 180) / Math.PI;

  // T term (blocker #3): cos(h - pi/6) and cos(3h + pi/30)
  const T =
    1 -
    0.17 * Math.cos(hbar - Math.PI / 6) +
    0.24 * Math.cos(2 * hbar) +
    0.32 * Math.cos(3 * hbar + Math.PI / 30) -
    0.20 * Math.cos(4 * hbar - (7 * Math.PI) / 20);

  const SH = 1 + 0.015 * Cabpbar * T;

  const dTheta = (Math.PI / 6) * Math.exp(-((hbarDeg - 275) * (hbarDeg - 275)) / (25 * 25));
  const RT =
    -2 *
    Math.sqrt(Math.pow(Cabpbar, 7) / (Math.pow(Cabpbar, 7) + Math.pow(25, 7))) *
    Math.sin(2 * dTheta);

  const dLpTerm = dLp / SL;
  const dCpTerm = dCp / SC;
  const dHpTerm = dHp / SH;

  return Math.sqrt(
    dLpTerm * dLpTerm +
    dCpTerm * dCpTerm +
    dHpTerm * dHpTerm +
    RT * dCpTerm * dHpTerm
  );
}
